import { useState, useEffect, useCallback } from "react";
import {
  AlertTriangle, CheckCircle2, Circle, Clock, ExternalLink, ChevronLeft, ChevronRight,
  ChevronDown, Truck, Banknote, ShieldAlert, User, Car,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { fmtEUR, fmtDate } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import ChecklistPieces from "@/components/admin/ChecklistPieces";
import BlocCommandeUsine from "@/components/admin/BlocCommandeUsine";
import { BlocImmatriculation, BlocFinancement, BlocConditionsLivraison } from "@/components/admin/SousBlocs";
import { ACTIONS_BLOCAGE, MessageBlocageDialog } from "@/components/admin/ActionsBlocage";
import { FieldText, FieldSelect } from "@/components/admin/champs";

const ETAT_STYLE = {
  fait:     { icone: CheckCircle2, couleur: "#16A34A", label: "Fait" },
  en_cours: { icone: Clock, couleur: "#0369A1", label: "En cours" },
  bloque:   { icone: AlertTriangle, couleur: "#B91C1C", label: "Bloqué" },
  na:       { icone: Circle, couleur: "#9CA3AF", label: "N/A" },
  a_venir:  { icone: Circle, couleur: "#C7CDD8", label: "À venir" },
};

function calculerJalons(adm, controles, etape) {
  const fac = adm.facturation || {};
  const liv = adm.livraison || {};
  const cu = adm.commande_usine || {};
  const immat = adm.immatriculation || {};
  const codes = (adm.blocages || []).map((b) => b.code);
  const checklist = adm.checklist || [];
  const oblig = checklist.filter((c) => c.obligatoire);
  const okPieces = oblig.length > 0 && oblig.every((c) => ["valide", "non_applicable", "derogation"].includes(c.statut));
  const factureId = fac.facture_id;
  const nbAFournir = oblig.filter((c) => !["valide", "non_applicable", "derogation"].includes(c.statut)).length;

  const jalons = [];
  jalons.push({
    key: "controle", label: "Contrôle des pièces",
    etat: codes.includes("PIECE_MANQUANTE") || codes.includes("SIGNATURE_EN_ATTENTE") ? "bloque" : okPieces ? "fait" : "en_cours",
    resume: okPieces ? "Toutes les pièces obligatoires sont réglées" : `${nbAFournir} pièce(s) obligatoire(s) à traiter`,
  });
  const cuActif = cu.mode === "commande" || cu.mode === "agent" || cu.numero_oem || cu.date_prevue_actuelle;
  jalons.push({
    key: "commande", label: "Commande usine",
    etat: !cuActif && adm.perimetre === "vo" ? "na"
      : codes.includes("DATE_USINE_DEPASSEE") ? "bloque"
      : cu.statut_oem === "arrivee" ? "fait"
      : cuActif ? "en_cours" : "na",
    date: cu.date_prevue_actuelle,
    resume: cu.statut_oem ? `Statut OEM : ${cu.statut_oem}` : "Véhicule de stock ou non renseigné",
  });
  jalons.push({
    key: "immatriculation", label: "Immatriculation",
    etat: codes.includes("CG_NON_DEMANDEE") || codes.includes("CG_NON_RECUE") || codes.includes("CG_NON_RETIREE") ? "bloque"
      : ["remis_client", "non_applicable"].includes(immat.statut) ? "fait"
      : immat.statut && immat.statut !== "a_faire" ? "en_cours" : "a_venir",
    date: immat.date_reception_titre || immat.date_depot_demande,
    resume: immat.statut ? `Statut : ${immat.statut}` : "Demande non déposée",
  });
  const conds = liv.conditions || {};
  jalons.push({
    key: "preparation", label: "Préparation véhicule",
    etat: codes.includes("PREPARATION_EN_RETARD") ? "bloque" : conds.preparation_terminee ? "fait" : liv.date_prevue ? "en_cours" : "a_venir",
    resume: conds.preparation_terminee ? "Préparation terminée (OR clos)" : "Cochée automatiquement à la clôture de l'OR de préparation",
  });
  const manquesLiv = (controles?.livraison?.manques || []).length;
  jalons.push({
    key: "livraison", label: "Livraison",
    etat: etape === "livre" ? "fait" : manquesLiv > 0 && liv.date_prevue ? "bloque" : liv.date_prevue ? "en_cours" : "a_venir",
    date: liv.date_realisee || liv.date_prevue,
    resume: etape === "livre" ? `Livré le ${fmtDate(liv.date_realisee || "")}` :
      manquesLiv > 0 ? `${manquesLiv} condition(s) manquante(s) pour livrer` : "Conditions réunies",
    manques: controles?.livraison?.manques || [],
  });
  jalons.push({
    key: "facturation", label: "Facturation",
    etat: codes.includes("LIVRE_NON_FACTURE") ? "bloque" : factureId ? "fait" : "a_venir",
    date: fac.date_facture,
    resume: factureId ? `Facture ${fac.numero_facture} · ${fmtEUR(fac.montant_ttc || 0)}` : "Aucune facture émise",
  });
  jalons.push({
    key: "encaissement", label: "Encaissement",
    etat: fac.solde_encaisse ? "fait" : codes.includes("SOLDE_NON_ENCAISSE") ? "bloque" : factureId ? "en_cours" : "a_venir",
    date: fac.date_encaissement,
    resume: fac.solde_encaisse ? "Solde encaissé" :
      factureId ? `Reste à encaisser : ${fmtEUR(fac.reste_a_encaisser || 0)}` : "En attente de facturation",
  });
  const manquesClot = controles?.cloture?.manques || [];
  jalons.push({
    key: "cloture", label: "Clôture",
    etat: adm.statut === "solde" ? "fait" : manquesClot.length && etape === "livre" ? "bloque" : "a_venir",
    resume: adm.statut === "solde" ? "Dossier soldé" : manquesClot.join(" · ") || "Se solde automatiquement",
    manques: manquesClot,
  });
  return jalons;
}

// Chantier 3e : volet latéral (Sheet) — la liste reste visible, navigation ← → au clavier.
export default function PanneauAdministratif({ dossierId, onClose, onNavigate, navInfo }) {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [adm, setAdm] = useState(null);
  const [controles, setControles] = useState(null);
  const [jalonOuvert, setJalonOuvert] = useState(null);
  const [messageCode, setMessageCode] = useState(null);
  const [refus, setRefus] = useState(null); // {controle, message, manques, retry}
  const [motifDerog, setMotifDerog] = useState("");
  const canEdit = ["secretaire_commerciale", "admin"].includes(auth?.user?.role);

  const charger = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/dossiers/${dossierId}/administratif`),
        api.get(`/dossiers/${dossierId}/controles`),
      ]);
      setAdm(r1.data);
      setControles(r2.data);
    } catch (e) { toast.error(apiErr(e)); }
  }, [dossierId]);

  useEffect(() => { setAdm(null); setJalonOuvert(null); charger(); }, [charger]);

  const majAdm = (nouvelAdm) => {
    setAdm((prev) => ({ ...nouvelAdm, contexte: nouvelAdm.contexte || prev?.contexte }));
    api.get(`/dossiers/${dossierId}/controles`).then((r) => setControles(r.data)).catch(() => {});
  };

  const patchSousObjet = async (cle, valeurs) => {
    try {
      const r = await api.patch(`/dossiers/${dossierId}/${cle}`, { valeurs });
      majAdm(r.data);
      toast.success("Enregistré.");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const executerAvecControle = async (type) => {
    try {
      if (type === "facturer") {
        const r = await api.post(`/dossiers/${dossierId}/facturer`, {});
        toast.success(`Facture ${r.data.numero} émise.`);
      } else if (type === "livrer") {
        await api.post(`/dossiers/${dossierId}/etape`, { etape: "livre" });
        toast.success("Livraison confirmée. 🎉");
      } else if (type === "cg_deposee") {
        await api.patch(`/dossiers/${dossierId}/immatriculation`, {
          valeurs: { statut: "demande_deposee", date_depot_demande: new Date().toISOString().slice(0, 10) } });
        toast.success("Demande d'immatriculation déclarée déposée.");
      } else if (type === "reprise_stock") {
        const r = await api.post(`/dossiers/${dossierId}/entrer-reprise-stock`);
        toast.success(`Reprise entrée au stock (${r.data.vehicule?.numero_police || "VO"}).`);
      }
      charger();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.manques) setRefus({ ...d, retry: type });
      else toast.error(apiErr(e));
    }
  };

  const deroger = async () => {
    try {
      await api.post(`/dossiers/${dossierId}/deroger-controle`, { controle: refus.controle, motif: motifDerog });
      toast.success("Dérogation enregistrée (une seule transition, tracée dans l'audit).");
      const retry = refus.retry;
      setRefus(null); setMotifDerog("");
      if (retry) await executerAvecControle(retry);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const actionBlocage = (code) => {
    const action = ACTIONS_BLOCAGE[code];
    if (!action) return;
    if (action.type === "message") setMessageCode(code);
    else if (action.type === "lien_vente") navigate(`/ventes/${dossierId}`);
    else if (action.type === "lien_planning") navigate("/planning");
    else executerAvecControle(action.type);
  };

  const ctx = adm?.contexte || {};
  const jalons = adm ? calculerJalons(adm, controles, ctx.etape) : [];
  const livraisonPrete = controles?.livraison?.ok && ctx.etape === "facture";

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 overflow-y-auto" data-testid="panneau-admin">
        <SheetHeader className="sr-only">
          <SheetTitle>Dossier administratif</SheetTitle>
          <SheetDescription>Frise des jalons, blocages et actions du dossier.</SheetDescription>
        </SheetHeader>

        {!adm ? (
          <div className="p-6 text-[13px] text-[--idc-muted]">Chargement…</div>
        ) : (
          <>
            {/* En-tête contexte (chantier 3c) : de QUI on parle */}
            <div className="sticky top-0 z-10 bg-[#14213D] text-white px-5 py-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[15px] font-bold" data-testid="ctx-client">{ctx.client_nom || "Client"}</span>
                    <span className="text-[11px] font-mono bg-white/10 rounded px-1.5 py-0.5">{ctx.numero}</span>
                    <span className="text-[10.5px] uppercase font-bold bg-white/10 rounded px-1.5 py-0.5">{adm.perimetre}</span>
                  </div>
                  <div className="text-[12px] text-white/85 mt-1 flex items-center gap-2 flex-wrap" data-testid="ctx-vehicule">
                    <Car className="w-3.5 h-3.5" /> {ctx.vehicule}
                    {(ctx.immatriculation || ctx.vin) && <span className="font-mono text-white/70">{ctx.immatriculation || ctx.vin}</span>}
                  </div>
                  <div className="text-[11.5px] text-white/70 mt-0.5 flex items-center gap-3 flex-wrap">
                    <span><User className="w-3 h-3 inline mr-1" />{ctx.vendeur || "—"}</span>
                    {ctx.montant != null && <span>{fmtEUR(ctx.montant)}</span>}
                    {ctx.date_livraison && <span><Truck className="w-3 h-3 inline mr-1" />livraison {fmtDate(ctx.date_livraison)}</span>}
                    <span className="bg-white/10 rounded px-1.5 py-0.5 font-semibold">{adm.statut_label || adm.statut}</span>
                  </div>
                </div>
                {onNavigate && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => onNavigate(-1)} title="Dossier précédent (←)"
                      className="p-1.5 rounded hover:bg-white/10 disabled:opacity-30"
                      disabled={navInfo && navInfo.index <= 0} data-testid="panneau-nav-prev">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    {navInfo && <span className="text-[11px] text-white/70">{navInfo.index + 1}/{navInfo.total}</span>}
                    <button onClick={() => onNavigate(1)} title="Dossier suivant (→)"
                      className="p-1.5 rounded hover:bg-white/10 disabled:opacity-30"
                      disabled={navInfo && navInfo.index >= navInfo.total - 1} data-testid="panneau-nav-next">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 mt-2.5">
                <LienCtx onClick={() => navigate(`/ventes/${dossierId}`)} testId="ctx-lien-vente">Vente</LienCtx>
                {ctx.client_id && <LienCtx onClick={() => navigate(`/clients/${ctx.client_id}`)} testId="ctx-lien-client">Fiche client</LienCtx>}
                {ctx.vehicule_id && <LienCtx onClick={() => navigate(`/stock-vehicules/${ctx.vehicule_id}`)} testId="ctx-lien-vehicule">Fiche véhicule</LienCtx>}
              </div>
            </div>

            <div className="p-5 space-y-4">
              {/* Bouton livraison en évidence quand tout est prêt */}
              {livraisonPrete && canEdit && (
                <button onClick={() => executerAvecControle("livrer")}
                  className="w-full py-3 rounded-xl bg-[#16A34A] text-white font-bold text-[14px] hover:bg-[#15803D] transition-colors flex items-center justify-center gap-2"
                  data-testid="btn-confirmer-livraison">
                  <Truck className="w-4 h-4" /> Confirmer la livraison — conditions réunies
                </button>
              )}

              {/* Blocages : une ligne = un bouton (chantier 3b) */}
              {(adm.blocages || []).length > 0 && (
                <div className="idc-card p-4" data-testid="section-blocages">
                  <div className="text-[13px] font-semibold mb-2 flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4 text-[#B91C1C]" /> Blocages ({adm.blocages.length})
                  </div>
                  <ul className="space-y-2">
                    {adm.blocages.map((b) => {
                      const action = ACTIONS_BLOCAGE[b.code];
                      return (
                        <li key={b.code} className="flex items-center gap-2.5 p-2.5 rounded-lg border border-[--idc-border]"
                          data-testid={`blocage-${b.code}`}>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase shrink-0"
                            style={{ background: b.criticite === "critique" ? "#FEE2E2" : b.criticite === "elevee" ? "#FEF3C7" : "#EFF6FF",
                                     color: b.criticite === "critique" ? "#B91C1C" : b.criticite === "elevee" ? "#B45309" : "#1D4ED8" }}>
                            {b.criticite}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-medium">{b.libelle}</div>
                            <div className="text-[10.5px] text-[--idc-muted]">resp. {b.responsable} · depuis {(b.depuis || "").slice(0, 10)}</div>
                          </div>
                          {action && canEdit && (
                            <button onClick={() => actionBlocage(b.code)}
                              className="shrink-0 px-2.5 py-1.5 rounded-full bg-[#14213D] text-white text-[11px] font-semibold hover:bg-[#1E3A8A] transition-colors flex items-center gap-1.5"
                              data-testid={`action-blocage-${b.code}`}>
                              <action.icone className="w-3 h-3" /> {action.label}
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Frise des jalons (chantier 3d) */}
              <div className="idc-card p-4" data-testid="frise-jalons">
                <div className="text-[13px] font-semibold mb-3">Parcours du dossier</div>
                <ol className="relative">
                  {jalons.map((j, i) => {
                    const st = ETAT_STYLE[j.etat];
                    const ouvert = jalonOuvert === j.key;
                    return (
                      <li key={j.key} className="relative pl-8 pb-1" data-testid={`jalon-${j.key}`}>
                        {i < jalons.length - 1 && (
                          <span className="absolute left-[9px] top-6 bottom-0 w-px bg-[--idc-border]" />
                        )}
                        <st.icone className="absolute left-0 top-1 w-[19px] h-[19px]" style={{ color: st.couleur }} />
                        <button onClick={() => setJalonOuvert(ouvert ? null : j.key)}
                          className="w-full text-left py-1 group" data-testid={`jalon-toggle-${j.key}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[13px] font-semibold ${j.etat === "na" || j.etat === "a_venir" ? "text-[--idc-muted]" : "text-[--idc-text]"}`}>
                              {j.label}
                            </span>
                            <span className="text-[9.5px] font-bold uppercase px-1.5 py-0.5 rounded-full"
                              style={{ background: `${st.couleur}18`, color: st.couleur }}>{st.label}</span>
                            {j.date && <span className="text-[11px] text-[--idc-muted]">{fmtDate(j.date)}</span>}
                            <ChevronDown className={`w-3.5 h-3.5 text-[--idc-muted] ml-auto transition-transform ${ouvert ? "rotate-180" : ""}`} />
                          </div>
                          <div className="text-[11.5px] text-[--idc-muted]">{j.resume}</div>
                        </button>
                        {ouvert && (
                          <div className="mt-2 mb-3 p-3 bg-[#F8F9FB] rounded-lg border border-[--idc-border]"
                            data-testid={`jalon-detail-${j.key}`}>
                            <DetailJalon jalon={j} adm={adm} controles={controles} canEdit={canEdit}
                              dossierId={dossierId} ctx={ctx} majAdm={majAdm}
                              patchSousObjet={patchSousObjet} executer={executerAvecControle}
                              setMessageCode={setMessageCode} />
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>

              {/* Dérogations */}
              {(adm.derogations || []).length > 0 && (
                <div className="idc-card p-4">
                  <div className="text-[13px] font-semibold mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#B45309]" /> Dérogations
                  </div>
                  <ul className="text-[12px] space-y-1" data-testid="derogations-liste">
                    {adm.derogations.map((d, i) => (
                      <li key={d.id || `${d.le}-${i}`} className="text-[--idc-muted]">
                        <span className="font-mono">{(d.le || "").slice(0, 10)}</span> ·{" "}
                        <b>{d.controle || d.code_controle}</b> · {d.motif}
                        <span className="opacity-70"> — {d.par}</span>
                        {d.type === "controle" && (
                          <span className={`ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${d.consommee ? "bg-[#E5E7EB] text-[#374151]" : "bg-[#FEF3C7] text-[#B45309]"}`}>
                            {d.consommee ? "consommée" : "active"}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </>
        )}

        {messageCode && (
          <MessageBlocageDialog dossierId={dossierId} code={messageCode}
            onClose={() => setMessageCode(null)} onEnvoye={charger} />
        )}

        {/* Refus de contrôle : liste NOMMÉE + dérogation motivée */}
        {refus && (
          <Dialog open onOpenChange={(o) => { if (!o) { setRefus(null); setMotifDerog(""); } }}>
            <DialogContent className="max-w-lg" data-testid="refus-controle-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-[#B91C1C]">
                  <ShieldAlert className="w-4 h-4" /> Contrôle « {refus.controle} » refusé
                </DialogTitle>
                <DialogDescription>Éléments manquants — traitez-les ou dérogez avec un motif tracé.</DialogDescription>
              </DialogHeader>
              <ul className="space-y-1" data-testid="refus-manques">
                {(refus.manques || []).map((m) => (
                  <li key={m} className="text-[13px] flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-[#B45309] mt-0.5 shrink-0" /> {m}
                  </li>
                ))}
              </ul>
              {canEdit && (
                <div className="pt-2 border-t border-[--idc-border]">
                  <p className="text-[11.5px] text-[--idc-muted] mb-1.5">
                    Dérogation (une seule transition, tracée dans l'audit) — motif obligatoire :
                  </p>
                  <textarea rows={2} value={motifDerog} onChange={(e) => setMotifDerog(e.target.value)}
                    className="w-full text-[13px] border border-[--idc-border] rounded p-2"
                    placeholder="Ex : accord du directeur, pièce transmise ce jour par e-mail"
                    data-testid="refus-derog-motif" />
                  <div className="flex justify-end gap-2 mt-2">
                    <Button variant="outline" onClick={() => { setRefus(null); setMotifDerog(""); }}>Fermer</Button>
                    <Button onClick={deroger} disabled={!motifDerog.trim()} data-testid="refus-deroger-btn">
                      Déroger et réessayer
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}
      </SheetContent>
    </Sheet>
  );
}

function LienCtx({ onClick, children, testId }) {
  return (
    <button onClick={onClick}
      className="text-[11px] font-semibold bg-white/10 hover:bg-white/20 rounded-full px-2.5 py-1 transition-colors flex items-center gap-1"
      data-testid={testId}>
      <ExternalLink className="w-3 h-3" /> {children}
    </button>
  );
}

function DetailJalon({ jalon, adm, controles, canEdit, dossierId, ctx, majAdm, patchSousObjet, executer, setMessageCode }) {
  const fac = adm.facturation || {};
  switch (jalon.key) {
    case "controle":
      return <ChecklistPieces dossierId={dossierId} checklist={adm.checklist} canEdit={canEdit} onMaj={majAdm} />;
    case "commande":
      return <BlocCommandeUsine adm={adm} canEdit={canEdit} onPatch={(v) => patchSousObjet("commande-usine", v)} />;
    case "immatriculation":
      return <BlocImmatriculation adm={adm} canEdit={canEdit} dossierId={dossierId}
        onPatch={(v) => patchSousObjet("immatriculation", v)} />;
    case "preparation":
    case "livraison":
      return (
        <div className="space-y-3">
          <BlocConditionsLivraison adm={adm} canEdit={canEdit} onPatch={(v) => patchSousObjet("livraison-admin", v)} />
          {jalon.key === "livraison" && (jalon.manques || []).length > 0 && (
            <ul className="text-[12px] space-y-0.5" data-testid="livraison-manques">
              {jalon.manques.map((m) => (
                <li key={m} className="text-[#B45309] flex items-start gap-1.5">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {m}
                </li>
              ))}
            </ul>
          )}
          {jalon.key === "livraison" && canEdit && ctx.etape === "facture" && (
            <Button size="sm" onClick={() => executer("livrer")} data-testid="jalon-livrer-btn">
              <Truck className="w-3.5 h-3.5 mr-1.5" /> Confirmer la livraison
            </Button>
          )}
          <BlocFinancement adm={adm} canEdit={canEdit} onPatch={(v) => patchSousObjet("financement", v)} />
        </div>
      );
    case "facturation":
      return (
        <div className="space-y-2 text-[12.5px]">
          {fac.facture_id ? (
            <>
              <p>Facture <b>{fac.numero_facture}</b> du {fmtDate(fac.date_facture || "")} · {fmtEUR(fac.montant_ttc || 0)}</p>
              {fac.date_echeance && <p className="text-[--idc-muted]">Échéance : {fmtDate(fac.date_echeance)}</p>}
            </>
          ) : (
            <>
              <p className="text-[--idc-muted]">Aucune facture émise. La facturation écrit automatiquement ce jalon (zéro ressaisie).</p>
              {canEdit && ["bdc", "a_livrer", "livre"].includes(ctx.etape) && (
                <Button size="sm" onClick={() => executer("facturer")} data-testid="jalon-facturer-btn">
                  <Banknote className="w-3.5 h-3.5 mr-1.5" /> Facturer maintenant
                </Button>
              )}
            </>
          )}
        </div>
      );
    case "encaissement":
      return <DetailEncaissement adm={adm} canEdit={canEdit} dossierId={dossierId}
        majAdm={majAdm} setMessageCode={setMessageCode} />;
    case "cloture":
      return (
        <div className="text-[12.5px] space-y-1">
          {(controles?.cloture?.manques || []).length === 0 ? (
            <p className="text-[#16A34A] font-medium">Conditions de clôture réunies — le statut passera automatiquement à « Soldé ».</p>
          ) : (
            controles.cloture.manques.map((m) => (
              <p key={m} className="text-[#B45309] flex items-start gap-1.5">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {m}
              </p>
            ))
          )}
        </div>
      );
    default:
      return null;
  }
}

function DetailEncaissement({ adm, canEdit, dossierId, majAdm, setMessageCode }) {
  const fac = adm.facturation || {};
  const [montant, setMontant] = useState("");
  const [mode, setMode] = useState("virement");
  const [envoi, setEnvoi] = useState(false);
  const encaissements = fac.encaissements || [];

  const encaisser = async () => {
    if (envoi) return;
    setEnvoi(true);
    try {
      const r = await api.post(`/dossiers/${dossierId}/encaissement`,
        { montant: parseFloat(montant), mode_reglement: mode });
      toast.success(r.data.solde_encaisse ? "Solde encaissé — facture soldée. 🎉"
        : `Encaissé. Reste : ${fmtEUR(r.data.reste_a_encaisser)}`);
      majAdm(r.data.administratif);
      setMontant("");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setEnvoi(false); }
  };

  return (
    <div className="space-y-3 text-[12.5px]" data-testid="detail-encaissement">
      {!fac.facture_id ? (
        <p className="text-[--idc-muted]">En attente de facturation.</p>
      ) : fac.solde_encaisse ? (
        <p className="text-[#16A34A] font-medium">Solde encaissé le {fmtDate(fac.date_encaissement || "")}.</p>
      ) : (
        <>
          <p>Reste à encaisser : <b>{fmtEUR(fac.reste_a_encaisser || 0)}</b>
            {fac.date_echeance && <span className="text-[--idc-muted]"> · échéance {fmtDate(fac.date_echeance)}</span>}</p>
          {canEdit && (
            <div className="flex items-end gap-2 flex-wrap">
              <div className="w-32"><FieldText label="Montant (€)" type="number" value={montant}
                onChange={setMontant} testId="enc-montant" /></div>
              <div className="w-36"><FieldSelect label="Mode" value={mode} onChange={setMode}
                options={[["virement", "Virement"], ["cheque", "Chèque"], ["carte", "Carte"],
                  ["especes", "Espèces"], ["financement", "Financement"]]} testId="enc-mode" /></div>
              <Button size="sm" onClick={encaisser} disabled={!parseFloat(montant) || envoi} data-testid="enc-valider">
                {envoi ? "Encaissement…" : "Encaisser"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setMessageCode("SOLDE_NON_ENCAISSE")}
                data-testid="enc-relancer">Relancer le règlement</Button>
            </div>
          )}
        </>
      )}
      {encaissements.length > 0 && (
        <ul className="space-y-0.5" data-testid="enc-historique">
          {encaissements.map((e) => (
            <li key={e.id} className="text-[11.5px] text-[--idc-muted]">
              {fmtDate(e.date)} · {fmtEUR(e.montant)} ({e.mode_reglement}) — {e.par}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
